import React, { useEffect } from 'react';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import { Copy, Check } from 'lucide-react';

interface CodeBlockProps {
  code: string;
  language: string;
  filename?: string;
}

export const CodeBlock: React.FC<CodeBlockProps> = ({ code, language, filename }) => {
  const [copied, setCopied] = React.useState(false);
  const codeLines = code.trim().split('\n');

  useEffect(() => {
    Prism.highlightAll();
  }, [code]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group my-12 animate-in fade-in slide-in-from-bottom-2 duration-500">
      {/* Editor Header */}
      <div className="bg-[#0b0e14] border border-slate-800/50 rounded-t-2xl px-5 py-4 flex items-center justify-between shadow-2xl">
        <div className="flex items-center gap-6">
          <div className="flex gap-2">
            <div className="w-3 h-3 rounded-full bg-rose-500/80 shadow-[0_0_10px_rgba(244,63,94,0.3)]" />
            <div className="w-3 h-3 rounded-full bg-amber-500/80 shadow-[0_0_10px_rgba(245,158,11,0.3)]" />
            <div className="w-3 h-3 rounded-full bg-emerald-500/80 shadow-[0_0_10px_rgba(16,185,129,0.3)]" />
          </div>
          {filename && (
            <div className="flex items-center gap-2">
              <div className="h-4 w-px bg-slate-800" />
              <span className="text-[11px] font-mono font-bold text-slate-500 uppercase tracking-widest">{filename}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span className="text-[10px] uppercase font-black tracking-[0.2em] text-primary/60">{language}</span>
          <button
            onClick={copyToClipboard}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-400 transition-all hover:text-white"
            title="Copy Code"
          >
            {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
          </button>
        </div>
      </div>

      {/* Editor Body */}
      <div className="relative shadow-2xl rounded-b-2xl overflow-hidden border-x border-b border-slate-800/50 group-hover:border-primary/20 transition-all">
        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-all" />
        
        <div className="flex bg-[#0d1117] font-mono text-sm leading-relaxed">
          {/* Line Numbers */}
          <div className="py-6 px-4 text-right bg-[#0b0e14]/50 border-r border-slate-800/30 select-none hidden sm:block">
            {codeLines.map((_, i) => (
              <div key={i} className="text-slate-600 text-[11px] h-[1.625rem]">{i + 1}</div>
            ))}
          </div>

          {/* Code Content */}
          <pre className={`language-${language} !my-0 !p-6 !bg-transparent !flex-1 overflow-x-auto selection:bg-primary/30`}>
            <code className={`language-${language} block`}>{code}</code>
          </pre>
        </div>

        {/* Shimmer Effect */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-white/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700" />
      </div>
    </div>
  );
};
